import {
  Box,
  CSSProperties,
  Tooltip as MuiTooltip,
  TooltipProps as MuiTooltipProps,
  Paper,
  TooltipComponentsPropsOverrides,
  Typography,
} from "@mui/material"
import { forwardRef, useEffect, useRef, useState } from "react"

export interface TooltipProps
  extends Omit<MuiTooltipProps, "title" | "children"> {
  title?: string
  description?: string
  children?: React.ReactElement
}
export interface TooltipContentProps extends TooltipComponentsPropsOverrides {
  title?: string
  description?: string
}

const TooltipContent = (props: TooltipContentProps) => {
  return (
    <Paper sx={{ borderRadius: "4px" }}>
      {props.title && (
        <Typography
          data-testid="testTooltipTitle"
          variant="subtitle1"
          gutterBottom
        >
          {props.title}
        </Typography>
      )}
      {props.description && (
        <Typography data-testid="testTooltipDescription" variant="body2">
          {props.description}
        </Typography>
      )}
    </Paper>
  )
}
export const Tooltip = forwardRef((props: TooltipProps, ref) => {
  const { title, description, children, ...rest } = props
  return (
    <MuiTooltip
      data-testid="testTooltip"
      title={<TooltipContent title={title} description={description} />}
      {...rest}
    >
      <Box>{children}</Box>
    </MuiTooltip>
  )
})
Tooltip.displayName = "Tooltip"

export interface TooltipButtonProps {
  message?: string
  showToolTip: boolean
  children: React.ReactElement
  maxWidth?: number | string
  childSx?: CSSProperties
}

type Position =
  | "bottom"
  | "left"
  | "right"
  | "top"
  | "top-start"
  | "top-end"
  | "bottom-start"

export const TooltipWrapper = ({
  message,
  showToolTip,
  children,
  maxWidth,
  childSx,
}: TooltipButtonProps) => {
  const [tooltipPosition, setTooltipPosition] = useState<Position>("top")
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      const windowHeight = window.innerHeight
      const windowWidth = window.innerWidth

      // Adjust tooltip placement based on component position
      if (rect.top < 50) {
        setTooltipPosition("bottom")
      } else if (rect.bottom > windowHeight - 50) {
        setTooltipPosition("top")
      } else if (rect.left < 50) {
        setTooltipPosition("right")
      } else if (rect.right > windowWidth - 50) {
        setTooltipPosition("left")
      } else {
        setTooltipPosition("top") // Default position
      }
    }
  }, [])
  if (showToolTip && message) {
    return (
      <Tooltip
        title={message}
        placement={tooltipPosition}
        slotProps={{
          tooltip: maxWidth ? { sx: { maxWidth } } : undefined,
        }}
      >
        <div style={childSx}>{children}</div>
      </Tooltip>
    )
  }

  return children
}
